/**
 * Collapsed list rows in an assistant message: `[[more-rows:N]]` + rows +
 * `[[/more-rows]]`.
 *
 * On the website a search shows more result cards than its text list prints.
 * The backend writes the rest of the list into the message anyway, wrapped in
 * these tokens, so the full list survives a reload; the bubble shows them
 * collapsed under a "Show N more options" toggle. Token shapes are owned by
 * sky360-ms `bot-agent/more-rows.ts` — change them there and here together.
 */
export type MoreRowsSegment =
  | { type: 'text'; value: string }
  | { type: 'more'; count: number; value: string };

const BLOCK_RE = /\[\[more-rows:(\d+)\]\]\n?([\s\S]*?)\n?\[\[\/more-rows\]\]/g;

export function parseMoreRows(text: string): MoreRowsSegment[] {
  if (!text || text.indexOf('[[more-rows:') === -1) return [{ type: 'text', value: text }];
  const out: MoreRowsSegment[] = [];
  let last = 0;
  BLOCK_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = BLOCK_RE.exec(text))) {
    // The toggle is a block of its own, so the line break that ended the last
    // listed row — and the one after the closing token — would each add an
    // empty line around it.
    let before = text.slice(last, match.index);
    if (before.endsWith('\n')) before = before.slice(0, -1);
    if (before) out.push({ type: 'text', value: before });
    out.push({ type: 'more', count: Number(match[1]), value: match[2] });
    last = match.index + match[0].length;
    if (text[last] === '\n') last += 1;
  }
  const rest = text.slice(last);
  if (rest) out.push({ type: 'text', value: rest });
  return out;
}
